/**
 * AiSuggestion — primitive Editorial Operations (HiFi.3).
 *
 * Bloc « citation » Nestor : fond sable, vignette ✦ blanche, kicker
 * « + SUGGESTION NESTOR » en sable foncé, texte en Fraunces italic
 * encadré de guillemets français « … ».
 *
 * **Ton bienveillant obligatoire** (CLAUDE.md §2.1). Aucun message
 * culpabilisant. Le composant n'enforce rien côté code — c'est au
 * pipeline AI Engine de produire du contenu correct (cf. tests
 * `tests/unit/ai-engine-tone.test.ts`).
 *
 * Maximum 3 suggestions visibles à la fois (§2.3) — c'est l'appelant
 * qui gère le filtrage / pagination, pas ce composant.
 */
import { html, type TemplateResult } from 'lit-html';
import styles from './editorial.module.css';
import { Icon, Icons } from './icons.js';

export interface AiSuggestionProps {
  /** Contenu textuel — toujours sans guillemets (le composant les ajoute). */
  text: string;
  /** Kicker custom (défaut : « Suggestion Nestor »). À traduire via t() côté appelant. */
  kicker?: string;
  /** Callback de fermeture — affiche un bouton X dans le coin. */
  onDismiss?: (e: Event) => void;
  /** testid pour les E2E. */
  testid?: string;
  /** ARIA label du bouton dismiss — à fournir traduit (« Fermer », « Dismiss »…). */
  dismissLabel?: string;
  /** testid du bouton dismiss (défaut `ai-suggestion-dismiss`). */
  dismissTestid?: string;
}

export function aiSuggestion(props: AiSuggestionProps): TemplateResult {
  const {
    text,
    kicker = 'Note de Nestor' /* i18n-allow — marque */,
    onDismiss,
    testid,
    dismissLabel = 'Fermer' /* i18n-allow — fallback */,
    dismissTestid = 'ai-suggestion-dismiss',
  } = props;
  return html`<div class=${styles.aiWrap}>
    <div
      class=${styles.aiBox}
      data-testid=${testid ?? 'ai-suggestion'}
      role="note"
      aria-label=${kicker}
    >
      <div class=${styles.aiIcon} aria-hidden="true">${Icon(Icons.spark, 16, { stroke: 1.6 })}</div>
      <div class=${styles.aiContent}>
        <div class=${styles.aiKicker}>${kicker}</div>
        <div class=${styles.aiText}>«&nbsp;${text}&nbsp;»</div>
        <div class=${styles.aiSign} aria-hidden="true">— Nestor</div>
      </div>
      ${onDismiss
        ? html`<button
            type="button"
            class=${styles.aiDismiss}
            @click=${onDismiss}
            aria-label=${dismissLabel}
            data-testid=${dismissTestid}
          >
            ${Icon(Icons.x, 14)}
          </button>`
        : null}
    </div>
  </div>`;
}
